import { useState, useEffect, useCallback } from 'react'
import { triageApi } from '../api'
import { useAuth } from '../AuthContext'
import { useEscapeKey } from '../useEscapeKey'
import { errorMessage } from './ErrorState'
import { enumLabel, useI18n, useT } from '../i18n'
import type { TriageKind, TriageStatus, TriageVocabulary, TriageLogEntry } from '../types'

/**
 * Traitement d'une détection : statut, prise en charge, motif, journal.
 *
 * Détecter sans tracer le traitement ne prouve rien. Ces contrôles sont ce qui
 * permet de démontrer qu'une détection a été examinée, par qui et quand.
 */

/** Chaque statut a une couleur qui dit s'il faut agir, sans lire le libellé. */
const STATUS_VARIANT: Record<TriageStatus, string> = {
  NEW: 'triage-new',
  TO_REVIEW: 'triage-todo',
  IN_REVIEW: 'triage-doing',
  CONFIRMED: 'triage-confirmed',
  FALSE_POSITIVE: 'triage-false',
  ESCALATED: 'triage-escalated',
  ARCHIVED: 'triage-archived',
}

export function TriageBadge({ status }: { status?: TriageStatus | null }) {
  const t = useT()
  const s = status ?? 'NEW'
  return <span className={`triage-badge ${STATUS_VARIANT[s]}`}>{enumLabel(t, 'triage', s, s)}</span>
}

interface Props {
  kind: TriageKind
  id: string
  status?: TriageStatus | null
  assignedTo?: string | null
  /** Appelé après tout changement, pour rafraîchir la liste appelante. */
  onChanged: () => void
}

export function TriageControls({ kind, id, status, assignedTo, onChanged }: Props) {
  const { user: currentUser } = useAuth()
  const { t, fmt } = useI18n()
  const current: TriageStatus = status ?? 'NEW'
  const [vocab, setVocab] = useState<TriageVocabulary | null>(null)
  const [target, setTarget] = useState<TriageStatus | null>(null)
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [log, setLog] = useState<TriageLogEntry[] | null>(null)

  useEffect(() => {
    triageApi.getVocabulary().then(setVocab).catch(() => setVocab(null))
  }, [])

  useEscapeKey(!!target, () => setTarget(null))
  useEscapeKey(!!log, () => setLog(null))

  const spec = vocab?.statuses.find((s) => s.value === current)
  const nextOptions = spec?.allowedNext ?? []
  const commentRequired = target
    ? (vocab?.statuses.find((s) => s.value === target)?.commentRequired ?? false)
    : false

  const submit = useCallback(async () => {
    if (!target) return
    setBusy(true)
    setError(null)
    try {
      await triageApi.changeStatus(kind, id, target, comment)
      setTarget(null)
      setComment('')
      onChanged()
    } catch (err: unknown) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }, [kind, id, target, comment, onChanged])

  const takeCharge = async () => {
    if (!currentUser?.email) return
    setBusy(true)
    try {
      // Relâcher si déjà prise en charge, sinon se l'attribuer.
      await triageApi.assign(kind, id, assignedTo ? null : currentUser.email)
      onChanged()
    } catch (err: unknown) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="triage-controls">
      <TriageBadge status={current} />

      {assignedTo && (
        <span className="triage-assignee" title={t('triage.assignedBy', { who: assignedTo })}>{assignedTo}</span>
      )}

      {nextOptions.length > 0 && (
        <select
          className="triage-select"
          value=""
          disabled={busy}
          aria-label={t('triage.changeStatus')}
          onChange={(e) => {
            const v = e.target.value as TriageStatus
            if (v) setTarget(v)
          }}
        >
          <option value="">{t('triage.handle')}</option>
          {nextOptions.map((s) => (
            <option key={s} value={s}>{enumLabel(t, 'triage', s, s)}</option>
          ))}
        </select>
      )}

      <button
        type="button"
        className="triage-link"
        onClick={takeCharge}
        disabled={busy}
      >
        {assignedTo ? t('triage.release') : t('triage.takeCharge')}
      </button>

      <button
        type="button"
        className="triage-link"
        onClick={async () => {
          try {
            setLog(await triageApi.getLog(kind, id))
          } catch (err: unknown) {
            setError(errorMessage(err))
          }
        }}
      >
        {t('triage.history')}
      </button>

      {error && <span className="triage-error">{error}</span>}

      {/* Changement de statut — le motif est exigé pour clore ou disculper. */}
      {target && (
        <div className="modal-overlay" onClick={() => setTarget(null)}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal-header">
              <h3>{enumLabel(t, 'triage', current, current)} → {enumLabel(t, 'triage', target, target)}</h3>
              <button type="button" className="modal-close" aria-label={t('common.close')} onClick={() => setTarget(null)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <label className="triage-comment-label">
                {commentRequired ? t('triage.reasonRequired') : t('triage.reasonOptional')}
                <textarea
                  className="triage-comment"
                  rows={4}
                  value={comment}
                  autoFocus
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={
                    commentRequired
                      ? t('triage.reasonPlaceholderRequired')
                      : t('triage.reasonPlaceholderOptional')
                  }
                />
              </label>
              {error && <div className="alert-error">{error}</div>}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setTarget(null)}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy || (commentRequired && !comment.trim())}
                onClick={submit}
              >
                {busy ? t('common.saving') : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Journal append-only : la preuve du traitement. */}
      {log && (
        <div className="modal-overlay" onClick={() => setLog(null)}>
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal-header">
              <h3>{t('triage.historyTitle')}</h3>
              <button type="button" className="modal-close" aria-label={t('common.close')} onClick={() => setLog(null)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              {log.length === 0 ? (
                <p className="import-help">{t('triage.historyEmpty')}</p>
              ) : (
                <ol className="triage-log">
                  {log.map((e, i) => (
                    <li key={i}>
                      <div className="triage-log-head">
                        <TriageBadge status={e.from} />
                        <span aria-hidden="true">→</span>
                        <TriageBadge status={e.to} />
                        <span className="triage-log-meta">
                          {fmt.dateTime(e.at)} · {e.agent}
                        </span>
                      </div>
                      {e.comment && <p className="triage-log-comment">{e.comment}</p>}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
